import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Icon } from './Icon';
import { ChannelLogo } from './ChannelLogo';

/**
 * مُرسِل الرسائل السريع — بهيئة قناته ورسالةٍ كاملة.
 *
 * يرسو في ركن الشاشة حيث يرسو كاتبُ البريد، ويلبس ألوان قناته. وفيه:
 * لوحةُ إيموجي مصنّفة كلوحات التطبيقات، وإرفاقُ صورةٍ أو فيديو أو ملف،
 * وتسجيلٌ صوتي من المتصفح، و«الرسائل المحفوظة» من الإعدادات تُدرَج
 * بنقرة. الوسائط على واتساب الرسمي — والبقية يقولها الخادم بصدق.
 *
 * SMS هنا أيضًا: تُكتب بالإيموجي والقوالب ثم تُسلَّم لتطبيق الرسائل في
 * الجهاز بنصّها — لا بوابة SMS مربوطة بعد، فلا نعلّمها «أُرسلت» زورًا.
 */

export type QuickChannel = 'WHATSAPP' | 'MESSENGER' | 'INSTAGRAM' | 'SMS';

const CHANNEL_CLASS: Record<QuickChannel, string> = {
  WHATSAPP: 'qm-wa',
  MESSENGER: 'qm-msgr',
  INSTAGRAM: 'qm-ig',
  SMS: 'qm-sms',
};

/* لوحة الإيموجي — مصنّفة كما في التطبيقات، بلا مكتبات. */
const EMOJI_GROUPS: Array<{ key: string; label: string; items: string }> = [
  {
    key: 'faces',
    label: '😀',
    items:
      '😀😃😄😁😆😅😂🤣🙂😉😊😇🥰😍🤩😘😗😚😙😋😛😜🤪😝🤗🤭🤫🤔🤐😐😑😶😏😒🙄😬😮‍💨🤥😌😔😪🤤😴😷🤒🤕🥴😵🤯🥳😎🤓🧐😕😟🙁☹️😮😯😲😳🥺😦😧😨😰😥😢😭😱😖😣😞😓😩😫🥱😤😡😠',
  },
  {
    key: 'hands',
    label: '👍',
    items:
      '👍👎👊✊🤛🤜👏🙌👐🤲🤝🙏✌️🤞🤟🤘👌🤌🤏👈👉👆👇☝️✋🤚🖐️🖖👋🤙💪🦾✍️💅🤳',
  },
  {
    key: 'hearts',
    label: '❤️',
    items:
      '❤️🧡💛💚💙💜🖤🤍🤎💔❣️💕💞💓💗💖💘💝💟♥️💯💢💥💫💦💨🕳️💬🗨️🗯️💭💤',
  },
  {
    key: 'work',
    label: '💼',
    items:
      '💼📁📂📅📆🗓️📇📈📉📊📋📌📍📎🖇️📏📐✂️🖊️🖋️✒️📝💻🖥️🖨️⌨️🖱️📱📞☎️📠📧📨📩📤📥📦🏷️💰💳🧾✈️🚗🏢🏠⏰⌛⏳',
  },
  {
    key: 'misc',
    label: '⭐',
    items:
      '⭐🌟✨⚡🔥🎉🎊🎁🏆🥇🥈🥉🎯🚀☀️🌙☁️🌈☕🍰🎂🌹🌸🌺💐🍀☑️✅❌❎➕➖➗✖️❓❗⚠️🔔🔕🔒🔓🔑',
  },
];

interface SavedTemplate {
  id: string;
  name: string;
  channel: string;
  body: string;
  isActive: boolean;
}

export function QuickMessageModal({
  channel,
  clientName,
  hint,
  pending,
  error,
  onSend,
  onClose,
}: {
  channel: QuickChannel;
  clientName: string;
  /** سطرُ صدقٍ تحت الحقل — أين تذهب الرسالة وما حدودها. */
  hint?: string;
  pending: boolean;
  error: string | null;
  onSend: (text: string, file: File | null) => void;
  onClose: () => void;
}) {
  const [text, setText] = useState('');
  const [panel, setPanel] = useState<'none' | 'emoji' | 'saved'>('none');
  const [emojiTab, setEmojiTab] = useState('faces');
  const [file, setFile] = useState<File | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const filePick = useRef<HTMLInputElement>(null);

  /* ---------- التسجيل الصوتي ---------- */
  const [recording, setRecording] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const [recErr, setRecErr] = useState<string | null>(null);

  const startRecording = async () => {
    setRecErr(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunks.current = [];
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: rec.mimeType || 'audio/webm' });
        setFile(new File([blob], `voice-${Date.now()}.webm`, { type: blob.type }));
        setRecording(false);
      };
      rec.start();
      recorder.current = rec;
      setRecording(true);
    } catch {
      setRecErr('تعذّر الوصول للميكروفون — راجع أذونات المتصفح.');
    }
  };

  useEffect(
    () => () => {
      if (recorder.current?.state === 'recording') recorder.current.stop();
    },
    [],
  );

  /* ---------- الرسائل المحفوظة ---------- */
  const saved = useQuery({
    queryKey: ['saved-messages'],
    queryFn: () => api<{ items: SavedTemplate[] }>('/callcenter/templates'),
    enabled: panel === 'saved',
    staleTime: 60_000,
  });
  const templates = (saved.data?.items ?? []).filter(
    (t) => t.isActive && (t.channel === channel || (channel === 'SMS' && t.channel === 'SMS')),
  );

  /* الإدراج في موضع المؤشّر — كما يفعل كل تطبيق مراسلة. */
  const insertAtCursor = (snippetText: string) => {
    const el = area.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const next = text.slice(0, start) + snippetText + text.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + snippetText.length, start + snippetText.length);
    });
  };

  /*
   * الإرفاق حيث تقبله الواجهة الرسمية: واتساب وماسنجر يرفعان الملف
   * مباشرة، وإنستغرام يشترط رابطًا عامًّا لا نملكه — فلا زرَّ يكذب.
   */
  const canAttach = channel === 'WHATSAPP' || channel === 'MESSENGER';
  const canSubmit = (text.trim().length > 0 || file) && !pending && !recording;

  return (
    <div className={`qm-composer ${CHANNEL_CLASS[channel]}`} role="dialog" aria-label="رسالة سريعة">
      <div className="qm-head">
        {channel === 'SMS' ? (
          <span className="qm-sms-badge">SMS</span>
        ) : (
          <ChannelLogo kind={channel} size={20} />
        )}
        <span className="qm-title">{clientName}</span>
        <button className="qm-x" onClick={onClose} aria-label="إغلاق">
          <Icon name="x" size={15} />
        </button>
      </div>

      <div className="qm-body">
        {error && <div className="alert alert-danger mb-3">{error}</div>}
        {recErr && <div className="alert alert-danger mb-3">{recErr}</div>}

        <textarea
          ref={area}
          className="qm-input"
          rows={4}
          maxLength={4000}
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
          placeholder={file && !text ? 'تعليقٌ مع الملف — اختياري…' : 'اكتب رسالتك…'}
        />

        {/* المرفق المنتظَر — يُرسَل مع النص أو وحده. */}
        {file && (
          <div className="qm-file">
            <Icon
              name={file.type.startsWith('audio/') ? 'sms' : file.type.startsWith('image/') ? 'download' : 'paperclip'}
              size={14}
            />
            <span className="tiny" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {file.name} · {(file.size / 1024).toFixed(0)}KB
            </span>
            <button className="qm-x-sm" onClick={() => setFile(null)} aria-label="إزالة الملف">
              <Icon name="x" size={12} />
            </button>
          </div>
        )}

        {recording && (
          <div className="qm-recording">
            <span className="cc-rec-dot" /> جارٍ التسجيل…
            <button className="btn btn-sm" onClick={() => recorder.current?.stop()}>
              إيقاف
            </button>
          </div>
        )}

        {panel === 'emoji' && (
          <div className="qm-emojis">
            <div className="qm-emoji-tabs">
              {EMOJI_GROUPS.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  className={`qm-emoji-tab${emojiTab === g.key ? ' is-on' : ''}`}
                  onClick={() => setEmojiTab(g.key)}
                >
                  {g.label}
                </button>
              ))}
            </div>
            <div className="qm-emoji-grid">
              {[...(EMOJI_GROUPS.find((g) => g.key === emojiTab)?.items ?? '')]
                .reduce<string[]>((acc, ch) => {
                  /* الإيموجي المركّب (متغيرات وممزوجات) يُجمع لا يُقسم حرفًا. */
                  const last = acc[acc.length - 1];
                  if (last && /[‍️]$/.test(last)) acc[acc.length - 1] = last + ch;
                  else if (/[‍️]/.test(ch) && last) acc[acc.length - 1] = last + ch;
                  else acc.push(ch);
                  return acc;
                }, [])
                .map((e, i) => (
                  <button
                    key={`${e}-${i}`}
                    type="button"
                    className="qm-emoji"
                    onClick={() => insertAtCursor(e)}
                  >
                    {e}
                  </button>
                ))}
            </div>
          </div>
        )}

        {panel === 'saved' && (
          <div className="qm-saved">
            {saved.isPending && <div className="skeleton" style={{ height: 60 }} />}
            {saved.isSuccess && templates.length === 0 && (
              <p className="tiny mute">
                لا رسائل محفوظة لهذه القناة — أضفها من «الإعدادات ← الرسائل المحفوظة».
              </p>
            )}
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                className="qm-saved-row"
                onClick={() => insertAtCursor(t.body)}
                title="أدرج في الرسالة"
              >
                <strong className="tiny">{t.name}</strong>
                <span className="tiny mute">{t.body.slice(0, 60)}</span>
              </button>
            ))}
          </div>
        )}

        {hint && <p className="tiny mute qm-hint">{hint}</p>}
      </div>

      <div className="qm-foot">
        <button
          type="button"
          className={`qm-tool${panel === 'emoji' ? ' is-on' : ''}`}
          onClick={() => setPanel((p) => (p === 'emoji' ? 'none' : 'emoji'))}
          title="إيموجي"
          aria-label="إيموجي"
        >
          🙂
        </button>
        <button
          type="button"
          className={`qm-tool${panel === 'saved' ? ' is-on' : ''}`}
          onClick={() => setPanel((p) => (p === 'saved' ? 'none' : 'saved'))}
          title="الرسائل المحفوظة"
          aria-label="الرسائل المحفوظة"
        >
          <Icon name="note" size={16} />
        </button>

        {canAttach && (
          <>
            <input
              ref={filePick}
              type="file"
              hidden
              accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setFile(f);
                if (filePick.current) filePick.current.value = '';
              }}
            />
            <button
              type="button"
              className="qm-tool"
              onClick={() => filePick.current?.click()}
              title="إرفاق صورة أو فيديو أو ملف"
              aria-label="إرفاق ملف"
            >
              <Icon name="paperclip" size={16} />
            </button>
            <button
              type="button"
              className={`qm-tool${recording ? ' is-on' : ''}`}
              onClick={() => (recording ? recorder.current?.stop() : void startRecording())}
              title={recording ? 'إيقاف التسجيل' : 'تسجيل صوتي'}
              aria-label="تسجيل صوتي"
            >
              🎤
            </button>
          </>
        )}

        <span style={{ flex: 1 }} />
        <button className="btn qm-send" disabled={!canSubmit} onClick={() => onSend(text.trim(), file)}>
          <Icon name="send" size={14} />
          {pending ? 'جارٍ الإرسال…' : channel === 'SMS' ? 'افتح في الرسائل' : 'أرسل'}
        </button>
      </div>
    </div>
  );
}
